import { initializationPromise } from '@/app/initialize';
import { activeRightStack, closeSidePanel } from '@/app/store';
import { syncProfileIfNotExist } from '@/app/sync/profiles';
import { handleJoinMeetLink } from '@/features/calendar/utils/meetLinkHelper';
import { validateInviteLink } from '@/infrastructure/sdk/invite';
import { useLanguage } from '@/localization/useLanguage';
import { VITE_API_URL } from '@/processENV';
import { formatUserIdToJid, getUserIdFromJid, showErrorToast } from '@/shared/utils';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setPendingToken } from '../model';

const extractInviteToken = () => window.location.pathname.split('/').filter(Boolean).pop() ?? null;

// Matches the token once the leading path segments are already stripped off by
// extractInviteToken — e.g. "call-ce0-b02d-jmq" (no leading slash). All three
// segments are alphanumeric — the first segment isn't letters-only (it can contain
// digits, e.g. "ce0").
const CALL_LINK_TOKEN_REGEX = /^call-([a-zA-Z0-9]{3}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{3})$/;

export const useInviteDeepLink = () => {
   const dispatch = useDispatch();
   const isLoggedIn = useSelector(state => state.auth.step === 'dashboard');
   const pendingToken = useSelector(state => state.inviteData?.pendingToken);
   const { t } = useLanguage();
   const isValidating = useRef(false);

   const handleToken = useCallback(
      async token => {
         if (!token || isValidating.current) return;
         isValidating.current = true;

         // Call/meet links don't need a server-side validation round trip up front —
         // the join preview screen validates the link itself via subscribeCall. Just
         // route straight there.
         if (CALL_LINK_TOKEN_REGEX.test(token)) {
            const apiBase = VITE_API_URL.replace('/api/v1', '') + '/call/';
            handleJoinMeetLink(`${apiBase}${token}`, dispatch);
            dispatch(setPendingToken(null));
            isValidating.current = false;
            return;
         }

         const res = await validateInviteLink(token);
         if (res?.statusCode === 200 && res.data?.fromUserId) {
            const { fromUserId } = res.data;
            await syncProfileIfNotExist(fromUserId);
            const activeChatId = getUserIdFromJid(fromUserId);
            dispatch(
               activeRightStack({
                  activeStack: 'chats',
                  activeChatId,
                  activeChatJid: formatUserIdToJid(activeChatId),
               }),
            );
            dispatch(closeSidePanel());
            dispatch(setPendingToken(null));
         } else if (res?.statusCode === 404 || res?.statusCode === 410) {
            dispatch(setPendingToken(null));
            showErrorToast(t('invite.EXPIRED_LINK'));
         } else {
            showErrorToast(res?.message || t('toast.ACTION_FAILED'));
         }

         isValidating.current = false;
      },
      [dispatch, t],
   );

   useEffect(() => {
      const processToken = async () => {
         const token = extractInviteToken();
         if (!token) return;

         window.history.replaceState({}, '', '/');

         await initializationPromise;

         if (isLoggedIn) {
            handleToken(token);
         } else {
            dispatch(setPendingToken(token));
         }
      };
      processToken();
   }, [dispatch, handleToken, isLoggedIn]);

   useEffect(() => {
      if (isLoggedIn && pendingToken) {
         handleToken(pendingToken);
      }
   }, [isLoggedIn, pendingToken, handleToken]);
};
